import { useEffect, useRef, useState } from 'react';
import {
  Modal, Button, Group, useMantineTheme, createStyles, Stack, NumberInput, Text, Divider, Center, List
} from '@mantine/core';
import { Upload, Plus, RotateClockwise, ArrowLeft, Home } from 'tabler-icons-react';
import { LoadAction } from '../editor/editor/actions/LoadAction';
import { showNotification } from '@mantine/notifications';
import { Masthead } from './Masthead';
import { createRoomRectangle } from '../editor/editor/roomStarter';
import { feetInchesToMeters } from '../editor/editor/units';
import startingRoom from '../templates/upstairs-front-room.json';

export function WelcomeModal() {
  const [opened, setOpened] = useState(false);
  const [step, setStep] = useState<'doors' | 'size' | 'where'>('doors');
  const fileRef = useRef<HTMLInputElement>();

  const [widthFeet, setWidthFeet] = useState<number>(12);
  const [widthInches, setWidthInches] = useState<number>(0);
  const [lengthFeet, setLengthFeet] = useState<number>(12);
  const [lengthInches, setLengthInches] = useState<number>(0);

  const useStyles = createStyles(() => ({ padded: { padding: '4px' } }));
  const theme = useMantineTheme();
  const { classes } = useStyles();

  useEffect(() => { setOpened(true); }, []);

  const hasLocalSave = typeof window !== 'undefined' && !!localStorage.getItem('autosave');

  const startNotice = {
    title: "Tools are down the left side",
    message: "Draw and drag walls, drop furniture in, and type exact lengths with the ruler tool. Press Help at any time.",
  };
  const dismiss = () => { setOpened(false); showNotification(startNotice); };

  const openStartingRoom = () => {
    new LoadAction(JSON.stringify(startingRoom)).execute();
    setOpened(false);
    showNotification({
      title: "The street side is the top wall",
      message: "The 21 ft width is off the record. The 13 ft 6 in depth is a guess, so measure that wall first and fix it with the ruler tool.",
    });
  };

  const loadFromDisk = async (e: any) => {
    const file = e.target.files.item(0);
    if (!file) { return; }
    const resultText = await file.text();
    try { JSON.parse(resultText); }
    catch {
      showNotification({ title: "That file would not open", message: "It does not look like a saved plan.", color: 'red' });
      return;
    }
    new LoadAction(resultText).execute();
    setOpened(false);
  };

  const buildRoom = () => {
    const width = feetInchesToMeters(widthFeet, widthInches);
    const length = feetInchesToMeters(lengthFeet, lengthInches);
    if (createRoomRectangle(width, length)) {
      setOpened(false);
      showNotification({
        title: "Four walls, at the size you typed",
        message: "Drag any corner to cut in an alcove or an angled wall. The ruler tool sets a wall to an exact length.",
      });
    }
  };

  return (
    <Modal
      className={classes.padded}
      closeOnClickOutside={false}
      closeOnEscape={false}
      opened={opened}
      withCloseButton={false}
      overlayColor={theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2]}
      overlayOpacity={0.55}
      centered
      size={step === 'where' ? 'lg' : 'md'}
      onClose={dismiss}
    >
      <Stack spacing="xs">
        <Center><Masthead /></Center>

        {step === 'doors' && (
          <>
            <Divider my="xs" />
            <Button onClick={openStartingRoom} leftIcon={<Home />} variant="light" size="md">
              Open the upstairs front room
            </Button>
            <Text size="xs" color="dimmed" align="center">
              21 ft across the window wall, 13 ft 6 in front to back.{' '}
              <Text component="span" size="xs" variant="link" inherit style={{ cursor: 'pointer' }} onClick={() => setStep('where')}>
                Where those numbers came from
              </Text>
            </Text>
            <Divider my="xs" variant="dotted" />
            <Button onClick={() => setStep('size')} leftIcon={<Plus />} variant="subtle">
              Start from my own measurements
            </Button>
            <input ref={fileRef} onChange={loadFromDisk} multiple={false} type="file" accept="application/json,.json,.txt" hidden />
            <Button onClick={() => fileRef.current.click()} leftIcon={<Upload />} variant="subtle">
              Open a plan someone sent me
            </Button>
            {hasLocalSave && (
              <Button
                onClick={() => {
                  const saved = localStorage.getItem('autosave');
                  if (saved) { new LoadAction(saved).execute(); }
                  setOpened(false);
                }}
                leftIcon={<RotateClockwise />}
                variant="subtle"
              >
                Pick up where I left off
              </Button>
            )}
            <Button onClick={dismiss} variant="subtle" color="gray">Start with a blank grid</Button>
          </>
        )}

        {step === 'where' && (
          <>
            <Divider my="xs" />
            <Text size="sm" weight={600}>Taken from the record</Text>
            <List size="sm" spacing="xs">
              <List.Item>
                The house body is 22 ft along the street by 28 ft deep, on a 616 sq ft
                footprint. That is off the city assessor's own building sketch, so it
                is as solid as this gets without a tape measure.
              </List.Item>
              <List.Item>
                Take off the two outside walls and the inside width across the front
                is about 21 ft. That is the wall the window sits in, and it is the top
                wall in this plan.
              </List.Item>
              <List.Item>
                Built 1927, one and a half storeys, 924 sq ft finished in total. Ground
                floor 616, which leaves about 308 upstairs.
              </List.Item>
            </List>

            <Text size="sm" weight={600} mt="sm">Guessed, so check it</Text>
            <List size="sm" spacing="xs">
              <List.Item>
                13 ft 6 in front to back is half the inside depth of the house. No
                public record says where the wall behind this room actually is. This
                is the first thing worth measuring.
              </List.Item>
              <List.Item>
                The roof ridge runs front to back and the gable faces the street, so
                the ceiling slopes down toward both long walls. A flat plan cannot
                show that. Measure how far in from each side wall the ceiling drops
                below head height, because a wardrobe will not fit outside that.
              </List.Item>
              <List.Item>
                Nothing here knows where the door, the stairs, or the closet are.
              </List.Item>
            </List>
            <Group position="apart" mt="md">
              <Button variant="subtle" color="gray" leftIcon={<ArrowLeft size={16} />} onClick={() => setStep('doors')}>Back</Button>
              <Button onClick={openStartingRoom}>Open it anyway</Button>
            </Group>
          </>
        )}

        {step === 'size' && (
          <>
            <Divider my="xs" />
            <Text size="sm">
              Measure wall to wall and type it in. These two numbers are only a
              starting rectangle, so a room with alcoves or angled walls still
              begins here: take the longest measurement each way, then drag the
              corners to match.
            </Text>
            <Group grow align="flex-end">
              <NumberInput label="Width, feet" value={widthFeet} onChange={(v) => setWidthFeet(v ?? 0)} min={1} max={100} />
              <NumberInput label="Inches" value={widthInches} onChange={(v) => setWidthInches(v ?? 0)} min={0} max={11} />
            </Group>
            <Group grow align="flex-end">
              <NumberInput label="Length, feet" value={lengthFeet} onChange={(v) => setLengthFeet(v ?? 0)} min={1} max={100} />
              <NumberInput label="Inches" value={lengthInches} onChange={(v) => setLengthInches(v ?? 0)} min={0} max={11} />
            </Group>
            <Group position="apart" mt="sm">
              <Button variant="subtle" color="gray" leftIcon={<ArrowLeft size={16} />} onClick={() => setStep('doors')}>Back</Button>
              <Button onClick={buildRoom}>Draw these walls</Button>
            </Group>
          </>
        )}

        <Text size="xs" color="dimmed" align="center" mt="xs">
          Built on Arcada by mehanix, Apache 2.0
        </Text>
      </Stack>
    </Modal>
  );
}
